// ffmpeg -i blocks.mov -an -vcodec h264 -acodec mp2 blocks.mp4
// ffmpeg -i blocks.mp4 -an -filter:v "crop=iw:ih-248:0:248" blocks_crop.mp4
// ffmpeg -i blocks_crop.mp4 -vf "scale=2080:-2" -c:a copy output.mp4
// ffmpeg -i output.mp4 -vf "select=eq(n\,0)" -q:v 3 blocks.jpg

// ffmpeg -i blocks.mp4 -filter:v "setpts=0.125*PTS" blocks_fast.mp4
// ffmpeg -i blocks_fast.mp4 -an -filter:v "crop=iw-1:ih-1:0:0" blocks_crop.mp4

import React, { Component } from "react";

import Menu from "./menu";

import LogoSVG from "./images/flexhouse/logo.svg";
import blocksMP4 from "./images/flexhouse/blocks.mp4";
import blocksJPG from "./images/flexhouse/blocks.jpg";
import nest1MP4 from "./images/flexhouse/nest1.mp4";
import nest1JPG from "./images/flexhouse/nest1.jpg";
import nest2MP4 from "./images/flexhouse/nest2.mp4";
import nest2JPG from "./images/flexhouse/nest2.jpg";
import cutsheetMP4 from "./images/flexhouse/cutsheet.mp4";
import cutsheetJPG from "./images/flexhouse/cutsheet.jpg";
import framerMP4 from "./images/flexhouse/framer.mp4";
import framerJPG from "./images/flexhouse/framer.jpg";

import "./css/flexhouse.css";

class Flexhouse extends Component {
  render() {
    return (
      <div className="flexhouse-page">
        <Menu type="projects" />
        <div className="intro section-wrapper">
          <img src={LogoSVG} className="logo" alt="Flexhouse" />
          <div className="credits">
            Drone shot of tract housing in Pennsylvania by{" "}
            <a href="https://halbergman.com" target="_blank" rel="noopener noreferrer">
              Hal Bergman
            </a>
          </div>
          <div className="section">
            <div className="content">
              <div className="intro-quote">
                <h1>This&nbsp;image&nbsp;is not&nbsp;a&nbsp;render</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="section-wrapper">
          <div className="section context">
            <div className="column">
              <h1>We want everyone to have control over their physical environment</h1>
              <h2>Opportunity</h2>
              <p>
                Custom homes are expensive to design and build, and one of the main reasons is the design tooling. Only
                20% of custom homes involve a licensed architect, yet everyone is still using unstructured tools that
                feel more like clay than Legos. As a result, the standard for new housing construction is superficial
                customization—just an illusion of choice.
              </p>
              <p>
                Our solution is a tool that allows for experimentation without the cost penalties, by designing within a
                system of real-world constraints and providing instant feedback on the impact of changes. By
                standardizing systems of building rather than construction plans, we can offer the same benefits as
                prefab while giving people more control over their physical environment.
              </p>
              <h2>Introduction</h2>
              <p>
                After going through a multi-year residential project,{" "}
                <a href="https://benbarry.com/" target="_blank" rel="noopener noreferrer">
                  Ben Barry
                </a>{" "}
                saw big opportunities to improve the experience for both homeowners and builders. Together with{" "}
                <a href="https://www.gndclouds.earth" target="_blank" rel="noopener noreferrer">
                  Will Felker
                </a>
                , we began working on{" "}
                <a href="https://flex.house" target="_blank" rel="noopener noreferrer">
                  Flexhouse
                </a>{" "}
                early summer of 2024. I joined as a co-founder and my role was to lead the engineering efforts.
              </p>
              <p>
                Rather than trying to design the perfect system upfront for something as complex as a home, we decided
                to build a series of prototypes, each increasing in complexity, learning as we went.
              </p>
              <div className="roadmap"></div>
              <p>Presented below are three of the prototypes.</p>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Toy blocks</h2>
              <p>
                Since this was my first Three.js project, we started with the simplest form of construction we could
                think of: toy blocks. The biggest challenges were related to interaction—specifically, stacking objects
                in 3D space using a 2D cursor. With an eye on what was next, I also took the chance to dive into custom
                geometry generation. I was happy to see that many tools from my web toolkit translated well, thanks to{" "}
                <a href="https://github.com/pmndrs/react-three-fiber" target="_blank" rel="noopener noreferrer">
                  R3F
                </a>{" "}
                and other{" "}
                <a href="https://github.com/pmndrs" target="_blank" rel="noopener noreferrer">
                  Poimandres
                </a>{" "}
                projects.
              </p>
              <p>
                Ben was{" "}
                <a
                  href="https://www.instagram.com/p/DEAbHKsJJRf/?img_index=1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  inspired to use this project
                </a>{" "}
                as part of his holiday card and it's{" "}
                <a href="https://holiday.flexhouse.dev" target="_blank" rel="noopener noreferrer">
                  available to play with here
                </a>
                .
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto">
                <source src={blocksMP4} type="video/mp4" poster={blocksJPG} />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Birdhouses</h2>
              <p>
                Since birdhouses share the same basic structural elements as human houses, we planned to reuse the
                modeling tool, data model, and undo engine in the next prototype. In hindsight, that was a mistake. We
                ended up stuck on birdhouses for most of the summer, which we were okay with, but it became a challenge
                when trying to explain to others what we were working toward.
              </p>
              <h3>Walls</h3>
              <p>
                The structure data model is a{" "}
                <a href="https://en.wikipedia.org/wiki/Planar_graph" target="_blank" rel="noopener noreferrer">
                  planar graph
                </a>{" "}
                of grid-snapped vertices, with regions{" "}
                <a
                  href="https://sci.bban.top/pdf/10.1016/0167-8655%252893%252990104-l.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  automatically detected
                </a>{" "}
                as rooms. Crossing wall segments are split, and I’ve added range boolean operations like delete and
                union. The goal was to enable quickly sketching out a structure, and I’m really happy with how it turned
                out.
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto">
                <source src={nest1MP4} type="video/mp4" poster={nest1JPG} />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
            <div className="column">
              <h3>Roofs and Fixtures</h3>
              <p>
                These tools were easier to implement than the wall tool, but each still posed challenges. Math, so much
                math.
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto">
                <source src={nest2MP4} type="video/mp4" poster={nest2JPG} />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
            <div className="column">
              <h3>Fabrication</h3>
              <p>
                The tool generates cut sheets for single-pass CNC fabrication. The structure is flattened into 2D
                shapes, and adjacent panels are merged using a simple algorithm to minimize cuts and ensure the
                structure can be easily assembled. Finally, the shapes are packed and divided into standard plywood
                sheet sizes.
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto">
                <source src={cutsheetMP4} type="video/mp4" poster={cutsheetJPG} />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Framer</h2>
              <p>
                This proof of concept was meant to show that the tool we created for birdhouses can be applied to
                human-scale structures. It’s 90% the same code as above, though there was quite a bit of refactoring. It
                finally captures the vision we had when we started this project, and seeing it come to life felt really
                rewarding. Unfortunately, it may have been a case of “too little, too late.”
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto">
                <source src={framerMP4} type="video/mp4" poster={framerJPG} />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider conclusion">
          <div className="section">
            <div className="column">
              <h2>Looking ahead</h2>
              <p>
                This was my favorite type of work—the kind that pushes me just outside my comfort zone, both technically
                and creatively. I had a great time, and I’m really grateful to Ben and Will for the experience. While
                things didn’t work out (maybe it was too ambitious to try raising funds in 2024 without an AI play), we
                still firmly believe that the future of residential design lies in standardized systems of building and
                accessible tools that make play possible.
              </p>
            </div>
            <div className="column">
              <p>
                <a href="#top" className="to-top">
                  Back to top
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Flexhouse;



// WEBPACK FOOTER //
// ./src/flexhouse.js